import { In } from 'typeorm'
import { Resolver, Query, FieldResolver, Root, Args, Arg, Ctx, Directive, Float } from 'type-graphql'
import { Domain, getQueryBuilderFromListParams, getRepository, ListParam } from '@things-factory/shell'
import { User } from '@things-factory/auth-base'
import { KpiValue } from './kpi-value'
import { KpiValueList } from './kpi-value-type'
import { Kpi } from '../kpi/kpi'
import { KpiValueScoreService } from './kpi-value-score.service'
import { getDomainIdsWithAncestors } from '../utils/domain-inheritance'

@Resolver(KpiValue)
export class KpiValueQuery {
  @Directive('@privilege(category: "kpi", privilege: "query", domainOwnerGranted: true, superUserGranted: true)')
  @Query(returns => KpiValue!, { nullable: true, description: 'Fetch a single KPI value by its unique identifier.' })
  async kpiValue(
    @Arg('id', { description: 'Unique identifier of the KPI value to fetch.' }) id: string,
    @Ctx() context: ResolverContext
  ): Promise<KpiValue> {
    const { domain } = context.state
    const domainIds = await getDomainIdsWithAncestors(domain)

    return await getRepository(KpiValue).findOne({
      where: { domain: { id: In(domainIds) }, id }
    })
  }

  @Directive('@privilege(category: "kpi", privilege: "query", domainOwnerGranted: true, superUserGranted: true)')
  @Query(returns => KpiValueList, { description: 'To fetch multiple KpiValues' })
  async kpiValues(@Args(type => ListParam) params: ListParam, @Ctx() context: ResolverContext): Promise<KpiValueList> {
    const { domain } = context.state
    const domainIds = await getDomainIdsWithAncestors(domain)

    const queryBuilder = getQueryBuilderFromListParams({
      domain: undefined,
      params,
      repository: getRepository(KpiValue),
      searchables: ['kpi', 'group', 'valueDate']
    })

    queryBuilder.andWhere(`${queryBuilder.alias}.domain IN (:...ancestorDomainIds)`, {
      ancestorDomainIds: domainIds
    })

    const [items, total] = await queryBuilder.getManyAndCount()

    return { items, total }
  }

  /**
   * 같은 (domain, kpi, valueDate, kpiOrgScope) 좌표에서 version 이 가장 큰 row 만 반환.
   * 차트·최종 KPI 계산 등 "최신 상태" 만 필요한 사용처용.
   *
   * 구현: subquery 로 (domain, kpi, valueDate, kpiOrgScope) 그룹별 MAX(version) 를 구한 뒤
   * 본 테이블과 매칭. 기존 kpiValues 와 동일하게 ListParam (filters/sortings/pagination)
   * 지원, 도메인 inheritance 적용.
   */
  @Directive('@privilege(category: "kpi", privilege: "query", domainOwnerGranted: true, superUserGranted: true)')
  @Query(returns => KpiValueList, {
    description: 'Fetch latest-version KpiValues only — one row per (domain, kpi, valueDate, kpiOrgScope).'
  })
  async latestKpiValues(
    @Args(type => ListParam) params: ListParam,
    @Ctx() context: ResolverContext
  ): Promise<KpiValueList> {
    const { domain } = context.state
    const domainIds = await getDomainIdsWithAncestors(domain)

    const queryBuilder = getQueryBuilderFromListParams({
      domain: undefined,
      params,
      repository: getRepository(KpiValue),
      searchables: ['kpi', 'group', 'valueDate']
    })

    queryBuilder.andWhere(`${queryBuilder.alias}.domain IN (:...ancestorDomainIds)`, {
      ancestorDomainIds: domainIds
    })

    // 같은 (domain, kpi, valueDate, kpiOrgScope) 좌표에서 가장 높은 version 만 통과.
    // 운영 DB 가 PostgreSQL 이라 DISTINCT ON 활용 — TypeORM QueryBuilder 의 정식 메서드.
    queryBuilder
      .distinctOn([
        `${queryBuilder.alias}.domainId`,
        `${queryBuilder.alias}.kpiId`,
        `${queryBuilder.alias}.valueDate`,
        `${queryBuilder.alias}.kpiOrgScope`
      ])
      .addOrderBy(`${queryBuilder.alias}.domainId`, 'ASC')
      .addOrderBy(`${queryBuilder.alias}.kpiId`, 'ASC')
      .addOrderBy(`${queryBuilder.alias}.valueDate`, 'ASC')
      .addOrderBy(`${queryBuilder.alias}.kpiOrgScope`, 'ASC')
      .addOrderBy(`${queryBuilder.alias}.version`, 'DESC')

    const items = await queryBuilder.getMany()

    return { items, total: items.length }
  }

  @FieldResolver(type => Domain)
  async domain(@Root() kpiValue: KpiValue): Promise<Domain> {
    return kpiValue.domainId && (await getRepository(Domain).findOneBy({ id: kpiValue.domainId }))
  }

  @FieldResolver(type => User)
  async updater(@Root() kpiValue: KpiValue): Promise<User> {
    return kpiValue.updaterId && (await getRepository(User).findOneBy({ id: kpiValue.updaterId }))
  }

  @FieldResolver(type => User)
  async creator(@Root() kpiValue: KpiValue): Promise<User> {
    return kpiValue.creatorId && (await getRepository(User).findOneBy({ id: kpiValue.creatorId }))
  }

  @FieldResolver(type => Kpi)
  async kpi(@Root() kpiValue: KpiValue): Promise<Kpi | null> {
    if (!kpiValue.kpiId) return null
    return await getRepository(Kpi).findOneBy({ id: kpiValue.kpiId })
  }
}
